import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchActivitiesList as fetchActivitiesListApi,
  fetchActivityById as fetchActivityByIdApi,
  fetchActivityCategories as fetchActivityCategoriesApi,
  fetchActivityDetailsBySlug as fetchActivityDetailsBySlugApi,
  fetchActivityFilters as fetchActivityFiltersApi,
  fetchActivityHome as fetchActivityHomeApi,
  fetchCityExperiences as fetchCityExperiencesApi,
  searchActivities as searchActivitiesApi,
} from "@/services/api/activities";

const initialState = {
  home: null,
  list: [],
  listMeta: {
    totalCount: 0,
    pageIndex: 1,
    pageSize: 10,
    totalPages: 0,
  },
  detail: null,
  detailLoading: false,
  detailError: null,
  categories: [],
  cityExperiences: [],
  searchResults: [],
  filters: null,
  filtersRequestId: null,
  loading: false,
  loadingMore: false,
  error: null,
};

export const fetchActivityHome = createAsyncThunk(
  "activity/fetchHome",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchActivityHomeApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load activity home");
    }
  }
);

export const fetchSearchActivities = createAsyncThunk(
  "activity/search",
  async (payload, { rejectWithValue }) => {
    try {
      return await searchActivitiesApi(payload);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to search activities");
    }
  }
);

export const fetchActivitiesList = createAsyncThunk(
  "activity/fetchList",
  async (arg, { rejectWithValue }) => {
    try {
      const { append = false, ...params } = arg || {};
      const response = await fetchActivitiesListApi(params);
      return { response, append };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to load activities list"
      );
    }
  }
);

export const fetchActivityFilters = createAsyncThunk(
  "activity/fetchFilters",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchActivityFiltersApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load activity filters");
    }
  }
);

export const fetchActivities = fetchActivitiesList;

export const fetchActivityById = createAsyncThunk(
  "activity/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      return await fetchActivityByIdApi(id);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load activity");
    }
  }
);

export const fetchActivityDetailsBySlug = createAsyncThunk(
  "activity/fetchDetailsBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      return await fetchActivityDetailsBySlugApi(slug);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to load activity details"
      );
    }
  }
);

export const fetchActivityCategories = createAsyncThunk(
  "activity/fetchCategories",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchActivityCategoriesApi(params);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load activity categories"
      );
    }
  }
);

export const fetchCityExperiences = createAsyncThunk(
  "activity/fetchCityExperiences",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchCityExperiencesApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load city experiences");
    }
  }
);

function extractListPayload(payload) {
  let page = payload;

  if (Array.isArray(page)) {
    page = { data: page };
  } else if (
    page?.data &&
    !Array.isArray(page.data) &&
    (Array.isArray(page.data.data) || page.data.totalCount != null)
  ) {
    page = page.data;
  } else {
    page = page || {};
  }

  const rows = Array.isArray(page.data)
    ? page.data
    : Array.isArray(page.activities)
      ? page.activities
      : [];

  const list = rows.map((row) => row?.activity || row).filter(Boolean);

  return {
    list,
    meta: {
      totalCount: Number(page.totalCount) || 0,
      pageIndex: Number(page.pageIndex) || 1,
      pageSize: Number(page.pageSize) || 10,
      totalPages: Number(page.totalPages) || 0,
    },
  };
}

function getActivityId(item = {}) {
  return item.activityId ?? item.id ?? item.slug;
}

const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    clearActivityError(state) {
      state.error = null;
    },
    resetActivityState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityHome.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityHome.fulfilled, (state, action) => {
        state.loading = false;
        state.home = action.payload;
      })
      .addCase(fetchActivityHome.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSearchActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
      })
      .addCase(fetchSearchActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchActivitiesList.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.loadingMore = true;
        } else {
          state.loading = true;
          state.loadingMore = false;
        }
        state.error = null;
      })
      .addCase(fetchActivitiesList.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        const response = action.payload?.response || {};
        const payload = Array.isArray(response.data) ? response : response.data || response;
        const { list, meta } = extractListPayload(payload);

        if (action.payload?.append) {
          const existingIds = new Set(state.list.map((item) => getActivityId(item)));
          const uniqueItems = list.filter((item) => !existingIds.has(getActivityId(item)));
          state.list = [...state.list, ...uniqueItems];
        } else {
          state.list = list;
        }

        state.listMeta = meta;
      })
      .addCase(fetchActivitiesList.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
        if (!action.meta.arg?.append) {
          state.list = [];
        }
      })
      .addCase(fetchActivityById.fulfilled, (state, action) => {
        state.detail = action.payload?.data || action.payload || null;
      })
      .addCase(fetchActivityDetailsBySlug.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchActivityDetailsBySlug.fulfilled, (state, action) => {
        state.detailLoading = false;
        const raw = action.payload?.data || action.payload || null;
        state.detail = raw;
      })
      .addCase(fetchActivityDetailsBySlug.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        state.detail = null;
      })
      .addCase(fetchActivityCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchActivityFilters.pending, (state, action) => {
        state.filters = null;
        state.filtersRequestId = action.meta.requestId;
      })
      .addCase(fetchActivityFilters.fulfilled, (state, action) => {
        if (state.filtersRequestId && action.meta.requestId !== state.filtersRequestId) {
          return;
        }
        state.filters = action.payload?.data || action.payload || null;
      })
      .addCase(fetchActivityFilters.rejected, (state, action) => {
        if (state.filtersRequestId && action.meta.requestId !== state.filtersRequestId) {
          return;
        }
        state.filters = null;
      })
      .addCase(fetchCityExperiences.fulfilled, (state, action) => {
        state.cityExperiences = action.payload;
      });
  },
});

export const { clearActivityError, resetActivityState } = activitySlice.actions;
export default activitySlice.reducer;
